// frontend/src/contexts/LanguageContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Helper to get initial language
const getInitialLanguage = () => {
  const saved = localStorage.getItem('language');
  if (saved && ['en', 'de', 'fr'].includes(saved)) {
    return saved;
  }
  // Optional: fallback to browser language
  const browserLang = navigator.language.split('-')[0];
  if (['en', 'de', 'fr'].includes(browserLang)) {
    return browserLang;
  }
  return 'en';
};

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [language, setLanguage] = useState(getInitialLanguage); // ✅ Initialize correctly

  useEffect(() => {
    // Ensure i18n is in sync on mount (in case it was initialized differently)
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, [i18n, language]);

  const changeLanguage = (newLanguage) => {
    if (language === newLanguage) return; // Avoid unnecessary updates

    setLanguage(newLanguage);
    localStorage.setItem('language', newLanguage);
    i18n.changeLanguage(newLanguage);

    // Update backend session
    fetch('/api/session/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ language: newLanguage }),
    }).catch(err => {
      console.warn('Failed to update session language:', err);
    });
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};